import { z } from 'zod';
import type { HttpClient } from './http.js';

/**
 * Contract for the hire request form. Shared verbatim by the web app (client
 * validation) and the api (server validation) so the two cannot drift.
 */

export const ENGAGEMENT_TYPES = [
  'architecture_review',
  'contract_lead',
  'full_time',
  'talk_workshop',
] as const;

export type EngagementType = (typeof ENGAGEMENT_TYPES)[number];

/** "When" on the form — how soon the work would start. */
export const TIMINGS = ['immediately', 'within_a_month', 'within_a_quarter', 'exploring'] as const;

export type Timing = (typeof TIMINGS)[number];

/**
 * "Budget" on the form, optional. Bands rather than a figure: this is a first
 * contact, and a band is enough to tell whether the conversation is worth
 * both parties' time.
 */
export const BUDGETS = [
  'prefer_to_discuss',
  'under_10k',
  'from_10k_to_25k',
  'from_25k_to_75k',
  'over_75k',
] as const;

export type Budget = (typeof BUDGETS)[number];

export const contactRequestSchema = z.object({
  engagement: z.enum(ENGAGEMENT_TYPES),
  name: z.string().trim().min(1, 'Name is required').max(120),
  company: z.string().trim().max(120).optional(),
  email: z.email('A valid email address is required').max(254),
  problem: z.string().trim().min(20, 'Please describe the problem').max(4000),
  when: z.enum(TIMINGS),
  budget: z.enum(BUDGETS).optional(),
  /** Cloudflare Turnstile token; required only when Turnstile is configured. */
  turnstileToken: z.string().max(2048).optional(),
});

export type ContactRequest = z.infer<typeof contactRequestSchema>;

export const contactResponseSchema = z.object({
  reference: z.string(),
});

export type ContactResponse = z.infer<typeof contactResponseSchema>;

export interface ContactApi {
  submit(request: ContactRequest, signal?: AbortSignal): Promise<ContactResponse>;
}

export function createContactApi(http: HttpClient): ContactApi {
  return {
    submit(request, signal) {
      return http.request({
        method: 'POST',
        path: '/contact',
        schema: contactResponseSchema,
        body: request,
        ...(signal ? { signal } : {}),
      });
    },
  };
}
